import { useRouterState } from '@tanstack/react-router';
import {
  type ComponentType,
  createContext,
  type LazyExoticComponent,
  lazy,
  Suspense,
  useContext,
  useEffect,
} from 'react';
import { de } from '@/i18n/de';
import { LoadingBlock } from '@/states/loading-block';
import { ComingSoonPage } from '@/states/pages';
import { pageLoader } from './pages';
import type { RouteEntry } from './route-table';

/** Merker der Hülle: „nach dem nächsten Seitenaufbau die Überschrift fokussieren“ (nicht beim ersten Laden). */
export const FocusRequest = createContext<{ current: boolean }>({ current: false });

/** Fokus auf die Hauptüberschrift der Seite (Kriterium D-7: nach dem Seitenwechsel weiß die Tastatur, wo sie ist). */
export function FocusHeading() {
  const request = useContext(FocusRequest);
  // Der aufgelöste Pfad wechselt erst, wenn die neue Seite steht (nicht schon beim Anstoßen der Navigation).
  const pathname = useRouterState({
    select: (state) => state.resolvedLocation?.pathname ?? state.location.pathname,
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: der Pfad löst die Prüfung aus, auch wenn die Seite dieselbe bleibt
  useEffect(() => {
    const heading = document.querySelector<HTMLElement>('main h1');
    if (!request.current || heading === null) {
      return;
    }
    request.current = false;
    heading.setAttribute('tabindex', '-1');
    heading.focus();
  }, [pathname, request]);
  return null;
}

const cache = new Map<string, LazyExoticComponent<ComponentType> | null>();

function pageFor(file: string): LazyExoticComponent<ComponentType> | null {
  if (!cache.has(file)) {
    const loader = pageLoader(file);
    cache.set(file, loader === undefined ? null : lazy(loader));
  }
  return cache.get(file) ?? null;
}

/**
 * Zeigt die Seite zu einer Route: die Seitendatei der Welle 2, sobald sie existiert (lazy geladen), sonst den
 * Platzhalter „Diese Seite folgt bald.“ mit dem Titel aus der Navigation.
 */
export function PageHost({ file, title }: Pick<RouteEntry, 'file' | 'title'>) {
  const Page = pageFor(file);
  return (
    // key: jede Route bekommt ihre eigene Grenze, damit die Überschrift erst nach dem Laden der Seite fokussiert wird
    <Suspense key={file} fallback={<LoadingBlock height={240} />}>
      {Page === null ? <ComingSoonPage title={de.pages[title]} /> : <Page />}
      <FocusHeading />
    </Suspense>
  );
}
